/**
 * Schema-management config + schema fetching, template loading, and the
 * widgets/templates dispatcher.
 *
 * `fetchHistoryConfig` adopts `runAsyncAction` for spinner + error surface.
 */

import cloneDeep from "lodash/cloneDeep";
import { getStore } from "@store";
import schemaManagementAdapter from "@features/schema-management/adapters";
import { getJWT } from "@utils/browser-helpers";
import { givenTimeIsBeforeOther } from "@utils/time-helpers";
import { isEmptyObject } from "@utils/object-helpers";
import {
  generateBlankConfig,
} from "@features/sypi/_utils/sypi-interface-helpers";
import {
  schemaFormatter,
  convertPatternPropertiesSchemaToPropertiesSchema,
  logDiffs,
  getCustomizedUiSchema,
  getUiSchema,
} from "@features/schema-management/utils/schemaManagementHelpers";
import { generateAthosConfig } from "../athosInterface";
import { dispatchObjErrors } from "../sypiBrcInterface";
import { runAsyncAction } from "@utils/runAsyncAction";
import {
  showSpinner,
  stopSpinner,
  reportError,
  setAlertDock,
} from "@actions/global";
import {
  loadSchema,
  loadConfig,
  loadDefaultUiSchema,
  loadClientUiSchema,
  loadDefaultConfig,
  loadInitialSchema,
  loadMasterSchema,
  loadInitialConfig,
  loadModifiedSchemas,
  loadInitialModifiedSchema,
  loadModifiedMasterSchemas,
  loadImportedInputs,
  setActionTab,
  setActionTabs,
} from "@features/schema-management/actions";
import {
  ACTION_TAB,
  ACTION_TABS,
  ATHOS_DEFAULT_PATTERN_PROPERTY_KEY,
  REWARDS_MFE_ATHOS,
  STATEMENTS_MFE_ATHOS,
  SYF_CARD_ACCESS,
  SYPI_BRC,
} from "@constants/schema-management";
import {
  EditableLabel,
  CustomSmArrayFieldTemplate,
} from "../../form_customizations/formTemplates";
import {
  ArrayItemSwitchButton,
  CustomFeatureComponent,
} from "../../form_customizations/formWidgets";
import CustomArrayFieldTemplate from "@features/sypi/form-customizations/CustomArrayFieldTemplate";
import ColorPickerWidget from "@features/sypi/form-customizations/ColorPickerWidget";
import CustomTitleFieldTemplate from "@features/sypi/form-customizations/CustomTitleFieldTemplate";
import CustomStringFieldTemplate from "@features/sypi/form-customizations/CustomStringFieldTemplate";
import CustomFieldWrapperTemplate from "../../form_customizations/CustomFieldWrapperTemplate";
import CustomSMTitleFieldTemplate from "../../form_customizations/CustomSMTitleFieldTemplate";
import { checkForDuplicateClient } from "./sm-import";
import { mergeAndHandleVisibilitySettings } from "./sm-errors";

export const fetchHistoryConfig = async (store = getStore()) => {
  await runAsyncAction(
    store,
    async () => {
      const { fetchInitialConfigTimeStamp, isAthosConfig } =
        store.getState().schemaAllView;
      const historySet = await schemaManagementAdapter.fetchHistoryConfig({
        limit: 1,
      });
      const { configSchedules } = historySet || [];
      const lastScheduledConfig = configSchedules?.length
        ? configSchedules[0]
        : {};
      const { activeReleaseVersion } = store.getState().schema;
      if (
        !isEmptyObject(lastScheduledConfig) &&
        givenTimeIsBeforeOther(
          fetchInitialConfigTimeStamp,
          lastScheduledConfig?.liveScheduleDate
        ) &&
        activeReleaseVersion === lastScheduledConfig?.release_version
      ) {
        store.dispatch(
          setAlertDock({
            title: "Scheduled change went live.",
            message: `Scheduled change '${lastScheduledConfig.name}' for release version ${lastScheduledConfig.release_version} recently went live and the config was updated with the scheduled values.`,
          })
        );
        await fetchSchemaManagement(activeReleaseVersion, true, isAthosConfig);
      }
    },
    { errorAction: "load", errorSubject: "history config" }
  );
};

export const getUiSchemaTemplate = (schema, defaultUiSchema = {}) => {
  let uiSchema = cloneDeep(defaultUiSchema);
  const validSchema = typeof schema === "object" && !isEmptyObject(schema);
  if (validSchema) {
    uiSchema = getUiSchema(schema, uiSchema);
  }
  return uiSchema;
};

export const fetchSMConfig = async (release_version, configCTID = "") => {
  const config = await schemaManagementAdapter.fetchSMConfig(
    {
      release_version,
    },
    configCTID
  );
  return config;
};

export const fetchSchemaManagement = async (
  release_version = "",
  fetchNew = false,
  isAthosConfig = false,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const activeReleaseVersion = release_version
    ? release_version
    : store.getState().schema.activeReleaseVersion;
  const initialConfigCall =
    fetchNew || !store.getState().schemaAllView.rehydrated;
  const { formattedSchema, schema, isPatternPropertySchema } =
    await fetchAndLoadSchemaTemplate(activeReleaseVersion);
  const defaultConfig = isAthosConfig
    ? {}
    : store.getState().schemaAllView.defaultConfig;
  const defaultUiSchema = store.getState().schemaAllView.defaultUiSchema;
  let patternPropertyKeys = [];
  const schemaManagementClient = getJWT().schema_management_client;
  store.dispatch(setActionTabs(ACTION_TABS[schemaManagementClient]));
  store.dispatch(setActionTab(ACTION_TAB[schemaManagementClient]));
  let config =
    (initialConfigCall
      ? await fetchSMConfig(activeReleaseVersion)
      : store.getState().schemaAllView.schemaManagementConfig) || {};
  if (initialConfigCall) {
    if (typeof config === "object") {
      config = config.sm_config;
      patternPropertyKeys = isPatternPropertySchema
        ? Object.keys(config).sort()
        : patternPropertyKeys;
    } else if (typeof config === "string") {
      store.dispatch(
        reportError(
          `Configuration File for release version ${release_version} has not been created yet.
          Please contact the application development team for assistance. \n`
        )
      );
      config = defaultConfig;
    }
  }
  let updatedConfig = cloneDeep(config);
  const initialConfig = cloneDeep(config);
  let setInitial = true;
  let modifiedSchema = {};

  if (isPatternPropertySchema) {
    modifiedSchema = await modifyPatternPropertiesSchema(
      schema,
      patternPropertyKeys
    );
  }
  if (formattedSchema?.type === "object") {
    updatedConfig = mergeAndHandleVisibilitySettings(
      defaultConfig,
      updatedConfig,
      setInitial
    );
    dispatchObjErrors(updatedConfig, formattedSchema, activeReleaseVersion);
  }
  const loggedDiffs = logDiffs(initialConfig, updatedConfig);
  if (loggedDiffs && !isEmptyObject(loggedDiffs)) {
    store.dispatch(loadImportedInputs(loggedDiffs));
  }
  store.dispatch(loadInitialConfig(initialConfig));
  store.dispatch(loadConfig(updatedConfig));
  const updatedDefaultUiSchema = getUiSchemaTemplate(
    schemaManagementClient === REWARDS_MFE_ATHOS ||
      schemaManagementClient === STATEMENTS_MFE_ATHOS
      ? modifiedSchema
      : formattedSchema,
    defaultUiSchema
  );
  const uiSchema = getCustomizedUiSchema(
    updatedDefaultUiSchema,
    schemaManagementClient
  );
  if (
    schemaManagementClient === SYF_CARD_ACCESS ||
    schemaManagementClient === REWARDS_MFE_ATHOS ||
    schemaManagementClient === STATEMENTS_MFE_ATHOS
  ) {
    checkForDuplicateClient(updatedConfig, schemaManagementClient);
  }
  store.dispatch(loadDefaultUiSchema(defaultUiSchema));
  store.dispatch(loadClientUiSchema(uiSchema));
  store.dispatch(stopSpinner());
};

export const fetchAndLoadSchemaTemplate = async (
  release_version = "",
  store = getStore()
) => {
  const activeReleaseVersion = release_version
    ? release_version
    : store.getState().schema.activeReleaseVersion;
  const {
    formattedSchema,
    formattedMasterSchema,
    schema,
    isPatternPropertySchema,
  } = await fetchSchema(activeReleaseVersion);
  if (!isPatternPropertySchema) {
    const configTemplate =
      formattedSchema?.type === "array"
        ? []
        : generateBlankConfig(formattedSchema);
    store.dispatch(loadDefaultConfig(configTemplate));
  }
  store.dispatch(loadSchema(formattedSchema));
  store.dispatch(loadInitialSchema(formattedSchema));
  store.dispatch(loadMasterSchema(formattedMasterSchema));
  return { formattedSchema, schema, isPatternPropertySchema };
};

export const fetchSchema = async (activeReleaseVersion = "", ctid = "") => {
  let fetchedSchemas = {
    schema: {},
    formattedSchema: {},
    formattedMasterSchema: {},
    rawMasterSchema: {},
    schemaChanged: false,
    isPatternPropertySchema: false,
  };
  const schema = await schemaManagementAdapter.fetchSchema(
    { release_version: activeReleaseVersion },
    ctid
  );
  fetchedSchemas.schema = schema;
  const isPatternPropertySchema =
    schema?.config_schema?.hasOwnProperty("patternProperties");
  fetchedSchemas.formattedSchema = isPatternPropertySchema
    ? schema.config_schema
    : schemaFormatter(schema.config_schema);
  if (schema.schema_has_changed) {
    fetchedSchemas.schemaChanged = schema.schema_has_changed;
    fetchedSchemas.rawMasterSchema = cloneDeep(schema.master_schema);
    fetchedSchemas.formattedMasterSchema = isPatternPropertySchema
      ? schema.master_schema
      : schemaFormatter(schema.master_schema);
  }
  fetchedSchemas.isPatternPropertySchema = isPatternPropertySchema;
  return fetchedSchemas;
};

export const modifyPatternPropertiesSchema = async (
  schema,
  patternPropertyKeys,
  store = getStore()
) => {
  const isAthosConfig =
    schema.sm_client_feature === REWARDS_MFE_ATHOS ||
    schema.sm_client_feature === STATEMENTS_MFE_ATHOS;
  const modifiedSchema = schemaFormatter(
    convertPatternPropertiesSchemaToPropertiesSchema(
      schema.config_schema,
      patternPropertyKeys
    )
  );
  const defaultModifiedSchema = schemaFormatter(
    convertPatternPropertiesSchemaToPropertiesSchema(schema.config_schema, [
      isAthosConfig ? ATHOS_DEFAULT_PATTERN_PROPERTY_KEY : "",
    ])
  );
  const configTemplate = isAthosConfig
    ? generateAthosConfig(defaultModifiedSchema)
    : {};
  const initialModifiedSchema = cloneDeep(modifiedSchema);
  if (schema.schema_has_changed) {
    const modifiedMasterSchema = schemaFormatter(
      convertPatternPropertiesSchemaToPropertiesSchema(
        schema.master_schema,
        patternPropertyKeys
      )
    );
    store.dispatch(loadModifiedMasterSchemas(modifiedMasterSchema));
  }
  store.dispatch(loadModifiedSchemas(modifiedSchema, defaultModifiedSchema));
  store.dispatch(loadInitialModifiedSchema(initialModifiedSchema));
  store.dispatch(loadDefaultConfig(configTemplate));
  return modifiedSchema;
};

export const getTemplatesAndWidgets = (client) => {
  let widgets = {};
  let templates = {};
  switch (client) {
    case SYF_CARD_ACCESS:
      widgets = {
        CheckboxWidget: ArrayItemSwitchButton,
        TextWidget: EditableLabel,
      };
      templates = { ArrayFieldTemplate: CustomSmArrayFieldTemplate };
      break;
    case SYPI_BRC:
      widgets = {
        ColorWidget: ColorPickerWidget,
        TextWidget: CustomStringFieldTemplate,
        CheckboxWidget: CustomFeatureComponent,
      };
      templates = {
        FieldTemplate: CustomFieldWrapperTemplate,
        TitleFieldTemplate: CustomTitleFieldTemplate,
        ArrayFieldTemplate: CustomArrayFieldTemplate,
      };
      break;
    case REWARDS_MFE_ATHOS:
      widgets = {
        CheckboxWidget: ArrayItemSwitchButton,
        TextWidget: CustomStringFieldTemplate,
      };
      templates = {
        FieldTemplate: CustomFieldWrapperTemplate,
        TitleFieldTemplate: CustomSMTitleFieldTemplate,
        ArrayFieldTemplate: CustomArrayFieldTemplate,
      };
      break;
    case STATEMENTS_MFE_ATHOS:
      widgets = {
        TextWidget: CustomStringFieldTemplate,
      };
      templates = {
        FieldTemplate: CustomFieldWrapperTemplate,
        TitleFieldTemplate: CustomSMTitleFieldTemplate,
      };
      break;
  }
  return { widgets, templates };
};
